import React, { memo } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams, useSearchParams } from "react-router-dom"

import { CircularProgress, Grid, Step, StepLabel, Stepper } from "@mui/material"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { capitalize } from "lodash"

import { InfoContainer } from "../../components/core/InfoContainer"
import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import { FeedbackInsightList } from "../../types/generated/api/FeedbackInsightList"
import { InsightList } from "../../types/generated/api/InsightList"
import { Report } from "../../types/generated/api/Report"
import { getEnvVar, isEnvVariableTruthy } from "../../utils/genericHelpers"
import { CorrelationInsight } from "./Components/CorrelationInsight"
import { DecisionFunnel } from "./Components/DecisionFunnel"
import { FeatureComparison } from "./Components/FeatureComparison"
import { FeaturesProfiles } from "./Components/FeatureProfilingInsight"
import { FeaturesCountsDashboardInsight } from "./Components/FeaturesCountsDashboardInsights"
import { LabelCriteriaInsight } from "./Components/LabelCriteriaInsight"
import { LabelInsight } from "./Components/LabelInsight"
import { LabelReasonFeedbackInsight } from "./Components/LabelReasonFeedbackInsight/LabelReasonFeedbackInsight"
import { LabelReasonInsight } from "./Components/LabelReasonInsight"
import { LabelTimeCountInsight } from "./Components/LabelTimeCountInsight"
import { PredictionsPerEngine } from "./Components/PredictionsPerEngine"
import { ReportContext } from "./Context"
import { formatInsightsData } from "./helpers"
import type { ReportContainerProps } from "./interfaces"

/**
 * A top-level component for rendering insights within a dashboard based on the report status.
 * Manages the rendering logic for different dashboard states based on the report status.
 * Handles the following states:
 * - Loading: Shows a progress indicator
 * - Failed: Displays error message with failure reason
 * - Preparation/Pending/Running: Shows a stepper with current progress
 * - Cancelled: Shows cancellation message
 * - Complete: Renders ordered insights
 *
 * The component includes interactive elements such as a cancel button during report generation
 * and proper error handling with user-friendly messages.
 *
 * @param {ReportContainerProps} props - Props passed to the component
 * @returns {React.ReactElement | null} A React element representing the current state of the report
 */
export const ReportContainer = memo(function ReportContainer(
  props: Readonly<ReportContainerProps>,
): React.ReactElement {
  const { uuid, reportType, feedbackColumn } = props

  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const dashboardType = searchParams.get("tab")

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const { t } = useTranslation("insights", {
    keyPrefix: "reportContainer",
  })

  const isLabelTimeInsightEnabled = isEnvVariableTruthy(String(getEnvVar("LABEL_TIME_INSIGHT_ENABLED")))
  const isComparisonInsightEnabled = isEnvVariableTruthy(String(getEnvVar("COMPARISON_INSIGHT_ENABLED")))
  const isCorrelationInsightEnabled = isEnvVariableTruthy(String(getEnvVar("CORRELATION_INSIGHT_ENABLED")))
  const isProfilingInsightEnabled = isEnvVariableTruthy(String(getEnvVar("PROFILING_INSIGHT_ENABLED")))
  const isFunnelEnabled = isEnvVariableTruthy(String(getEnvVar("DECISION_FUNNEL_ENABLED")))

  // Fetch dashboard report data
  const {
    data: dashboardReport,
    isLoading: isLoadingDashboardReport,
    isError: isErrorDashboardReport,
  } = useQuery<AxiosResponse<Report>, AxiosError>(
    ["report", projectId, uuid],
    () =>
      KonanAPI.fetchInsightsReport({
        project_uuid: projectId as string,
        report_uuid: uuid,
      }),
    {
      enabled: !!uuid,
    },
  )

  // Mutation for cancelling report generation
  const cancelReportMutation = useMutation<AxiosResponse, AxiosError, { project_uuid: string; report_uuid: string }>(
    KonanAPI.cancelInsightsReport,
    {
      onSuccess: async () => {
        // Invalidate reports cache to trigger a refresh
        await queryClient.invalidateQueries(["dashboard-reports", projectId])
        NotificationUtils.toast(t("notifications.cancelReport.success"), { severity: "success" })
      },
      onError: () => {
        NotificationUtils.toast(t("notifications.cancelReport.error"), { severity: "error" })
      },
    },
  )

  /**
   * Renders the appropriate insight component based on the insight type.
   * Serves as a router to map insight types to their corresponding visualization components.
   *
   * @param {InsightList} insight - The insight object containing type and visualization data
   * @returns {React.ReactElement | null} The corresponding insight component or null if type is not supported
   */
  const insightSwitch = (insight: InsightList | FeedbackInsightList): React.ReactElement | null => {
    switch (insight.type) {
      case InsightList.type.LABEL:
      case FeedbackInsightList.type.FEEDBACK_COUNT:
        return (
          <LabelInsight
            insight={insight}
            isFeedback={insight.type === FeedbackInsightList.type.FEEDBACK_COUNT}
            reportType={reportType}
            isFeedbackColumn={!!feedbackColumn}
          />
        )

      case InsightList.type.LABEL_REASON:
        return <LabelReasonInsight insight={insight} reportType={reportType} isFeedbackColumn={!!feedbackColumn} />

      case FeedbackInsightList.type.FEEDBACK_PER_LABEL_AND_REASON: {
        if (reportType === "simulation") {
          return <LabelReasonFeedbackInsight insight={insight} />
        } else {
          return <LabelCriteriaInsight insight={insight} />
        }
      }

      case InsightList.type.FEATURES_COUNTS:
        return <FeaturesCountsDashboardInsight insight={insight} />

      case FeedbackInsightList.type.FEEDBACKS_PER_WORKFLOW_MODEL:
      case InsightList.type.PREDICTIONS_PER_MODEL:
      case InsightList.type.PREDICTIONS_PER_WORKFLOW:
        return (
          <PredictionsPerEngine
            insight={insight}
            engine={capitalize(currentProject?.category)}
            isFeedback={insight.type === FeedbackInsightList.type.FEEDBACKS_PER_WORKFLOW_MODEL}
          />
        )

      case InsightList.type.LABEL_TIME_COUNT:
        if (!isLabelTimeInsightEnabled) return null
        return <LabelTimeCountInsight insight={insight} />

      case InsightList.type.FEATURE_COMPARISON:
        if (!isComparisonInsightEnabled) return null
        return <FeatureComparison insight={insight} />

      case InsightList.type.FEATURE_CORRELATION:
      case FeedbackInsightList.type.FEEDBACK_CORRELATION:
        if (!isCorrelationInsightEnabled) return null
        return (
          <CorrelationInsight
            insight={insight}
            isFeedback={insight.type === FeedbackInsightList.type.FEEDBACK_CORRELATION}
          />
        )

      case InsightList.type.FEATURES_PROFILING:
      case FeedbackInsightList.type.FEEDBACK_PROFILE:
        if (!isProfilingInsightEnabled) return null
        return (
          <FeaturesProfiles insight={insight} isFeedback={insight.type === FeedbackInsightList.type.FEEDBACK_PROFILE} />
        )

      case InsightList.type.WORKFLOW_NODE_PERCENTAGES:
        if (!isFunnelEnabled) return null
        return <DecisionFunnel insight={insight} />

      default:
        return null
    }
  }

  /**
   * Orders and filters insights based on a predefined type order.
   * This function ensures insights are displayed in a consistent, logical sequence
   * and handles deduplication by selecting only the first insight of each type.
   *
   * @param {InsightList[]} insights - An array of insight objects to be ordered
   * @returns {InsightList[]} An ordered array of unique insights, with one insight per type,
   *                         following the predefined sequence: LABEL, PREDICTIONS_PER_WORKFLOW,
   *                         LABEL_REASON, FEATURES_COUNTS, FEATURE_COMPARISON
   */
  const orderedInsights = (insights: InsightList[] | undefined): InsightList[] => {
    // Group insights by their type for efficient filtering
    const groupedInsights = Object.groupBy(insights ?? [], (insight) => insight?.type) ?? []

    // Define the desired order of insight types for consistent display
    const orderedTypes = [
      "LABEL",
      "PREDICTIONS_PER_WORKFLOW",
      "PREDICTIONS_PER_MODEL",
      "WORKFLOW_NODE_PERCENTAGES",
      "LABEL_REASON",
      "FEATURES_COUNTS",
      "FEATURE_COMPARISON",
      "LABEL_TIME_COUNT",
      "FEATURE_CORRELATION",
      "FEATURES_PROFILING",
    ]

    const simulationReportOrderedInsights = [
      "LABEL",
      "FEEDBACK_COUNT",
      "FEEDBACK_PER_LABEL_AND_REASON",
      "PREDICTIONS_PER_WORKFLOW",
      "PREDICTIONS_PER_MODEL",
      "LABEL_REASON",
      "WORKFLOW_NODE_PERCENTAGES",
      "FEATURES_COUNTS",
      "FEATURE_COMPARISON",
      "LABEL_TIME_COUNT",
      "FEATURE_CORRELATION",
      "FEATURES_PROFILING",
    ]

    // Map over ordered types to return in desired format
    if (reportType === "simulation") {
      return formatInsightsData(simulationReportOrderedInsights, groupedInsights)
    }
    // For non-simulation reports, use the default order
    return formatInsightsData(orderedTypes, groupedInsights)
  }

  /**
   * Orders and filters insights based on a predefined type order.
   * This function ensures insights are displayed in a consistent, logical sequence
   * and handles deduplication by selecting only the first insight of each type.
   *
   * @param {FeedbackInsightList[]} insights - An array of insight objects to be ordered
   * @returns {FeedbackInsightList[]} An ordered array of unique insights, with one insight per type,
   *                         following the predefined sequence: LABEL, PREDICTIONS_PER_WORKFLOW,
   *                         LABEL_REASON, FEATURES_COUNTS, FEATURE_COMPARISON
   */
  const orderedFeedbackInsights = (insights: FeedbackInsightList[] | undefined): FeedbackInsightList[] => {
    // Group insights by their type for efficient filtering
    const groupedInsights = Object.groupBy(insights ?? [], (insight) => insight?.type) ?? []

    // Define the desired order of insight types for consistent display
    const orderedTypes = [
      "FEEDBACK_COUNT",
      "FEEDBACKS_PER_WORKFLOW_MODEL",
      "FEEDBACK_PER_LABEL_AND_REASON",
      "FEEDBACK_CORRELATION",
      "FEEDBACK_PROFILE",
    ]

    // Map over ordered types to return in desired format
    return orderedTypes
      .map((type) => {
        const insightType = type as FeedbackInsightList.type
        return (groupedInsights[insightType] as FeedbackInsightList[])?.[0]
      })
      .filter((item) => !!item)
  }

  const insights =
    reportType === "simulation" && feedbackColumn && dashboardReport?.data
      ? [
          ...dashboardReport.data.feedback_insights.filter(
            (insight) => insight.type === "FEEDBACK_PER_LABEL_AND_REASON" || insight.type === "FEEDBACK_COUNT",
          ),
          ...dashboardReport.data.insights,
        ]
      : (dashboardReport?.data.insights as InsightList[])

  /**
   * ScreenStateSwitch Component
   *
   * Manages the rendering logic for different dashboard states based on the report status.
   * Handles the following states:
   * - Loading: Shows a progress indicator
   * - Failed: Displays error message with failure reason
   * - Preparation/Pending/Running: Shows a stepper with current progress
   * - Cancelled: Shows cancellation message
   * - Complete: Renders ordered insights
   *
   * The component includes interactive elements such as a cancel button during report generation
   * and proper error handling with user-friendly messages.
   *
   * @returns {React.ReactElement | null} A React element representing the current state of the report
   */

  const ScreenStateSwitch = (): React.ReactElement | null => {
    // Loading state - show progress indicator
    if (isLoadingDashboardReport)
      return (
        <Grid container item xs={12} height={"100%"} justifyContent={"center"}>
          <InfoContainer icon={<CircularProgress />} title={t("loading")} />
        </Grid>
      )

    // Error state - show empty state with error message if fetching insights report failed
    if (isErrorDashboardReport) {
      return (
        <Grid container item xs={12} height={"100%"} justifyContent={"center"}>
          <KonanEmptyState title={t("emptyStates.failed.title")} subTitle={t("emptyStates.failed.subtitle")} />
        </Grid>
      )
    }

    // Error state - show failure message with reason if available
    if (
      dashboardReport?.data?.status === Report.status.FAILED &&
      (!dashboardReport.data.insights || dashboardReport.data.insights.length === 0)
    )
      return (
        <Grid container item xs={12} height={"100%"} justifyContent={"center"}>
          <KonanEmptyState
            title={t("emptyStates.failed.title")}
            subTitle={dashboardReport?.data.failure_reason?.replaceAll("_", " ") ?? t("emptyStates.failed.subtitle")}
          />
          {/* TODO:: add date picker */}
        </Grid>
      )

    // Map status to stepper position for visual progress indication
    enum StatusStateMap {
      PENDING = 0,
      PREPARATION = 1,
      RUNNING = 2,
    }

    // Preparation state - show progress stepper and cancel option
    if (
      dashboardReport?.data?.status === Report.status.PREPARATION ||
      dashboardReport?.data?.status === Report.status.PENDING ||
      (dashboardReport?.data?.status === Report.status.RUNNING && dashboardReport.data.insights.length === 0)
    )
      return (
        <Grid container item xs={12} height={"100%"} justifyContent={"center"}>
          <Grid item xs={12} md={6} alignSelf={"center"} justifySelf={"center"}>
            <Stepper
              activeStep={StatusStateMap[dashboardReport?.data?.status as keyof typeof StatusStateMap]}
              alternativeLabel
            >
              {["Pending", "Preparing", "Running"].map((label) => (
                <Step key={label}>
                  <StepLabel>
                    <Typography variant={"a"} align="center">
                      {t(`stepper.${label.toLowerCase()}`, { defaultValue: label })}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <InfoContainer
              title={t("infoContainer.title")}
              action={
                <Button
                  key={"empty-state-cancel"}
                  variant="dangerous"
                  size="small"
                  isLoading={cancelReportMutation.isLoading}
                  disabled={cancelReportMutation.isLoading}
                  onClick={() =>
                    cancelReportMutation.mutateAsync({
                      project_uuid: projectId as string,
                      report_uuid: dashboardReport?.data?.uuid,
                    })
                  }
                >
                  {t("infoContainer.cancelReport")}
                </Button>
              }
            />
          </Grid>
        </Grid>
      )

    // Cancelled state - show abort message when no insights are available
    if (dashboardReport?.data?.status === Report.status.CANCELLED && dashboardReport?.data.insights.length === 0)
      return (
        <Grid container item xs={12} height={"100%"} justifyContent={"center"}>
          <KonanEmptyState title={t("emptyStates.aborted.title")} />
        </Grid>
      )

    return (
      <Grid container rowGap={2} columnSpacing={2}>
        {dashboardType === "feedback" && currentProject?.type !== "generic"
          ? orderedFeedbackInsights(dashboardReport?.data.feedback_insights as FeedbackInsightList[]).map(insightSwitch)
          : orderedInsights(insights as InsightList[]).map(insightSwitch)}
      </Grid>
    )
  }

  return (
    // Using spacing instead of gap on columns to avoid wrapping of components
    // This ensures consistent layout across different screen sizes
    <ReportContext.Provider value={{ report: dashboardReport?.data }}>
      <ScreenStateSwitch />
    </ReportContext.Provider>
  )
})
